import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Box, Grid, IconButton, List, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GC from '@mescius/spread-sheets';
import * as _ from 'lodash-es';

import { MultiSelect } from '../../MultiSelect';
import { DataCatalogDragBox } from '../DataCatalogDragBox';

import { useBusinessReportEditor } from '../../../services/store/Report';
import { useLoop } from '../../../services/store/Loop';
import { useSpread } from '../../../services/store/Spread';
import { useMappingProperty } from '../../../services/store/Mapping';
import { useError } from '../../../services/store/Error';

import { Option, Property, PropertyView } from '../../../types';
import { DraggableIcon } from '../../Icons';

import {
  COMMAND_MANAGER_REGISTER_PROPERTY,
  FILE_TYPE_EXCEL_XLSX,
  ALLOWED_FILE_TYPES,
  MAPPING_RESTRICTION,
} from '../../../constants';

import * as S from './styles';
import { isRangeContainedInRange } from '../../../utils/range';
import {
  addLoop,
  createLoopId,
  isRangeIntersectingAnyLoop,
} from '../../../utils/loop';
import { applyLoopBackground, clearCellBackground } from '../../../utils/style';
import { removePropertyByCell } from '../../../utils/property';
import { approvalFileType } from '../../../utils/file';
import { translate } from '../../../utils/translations';

export function DataCatalogHandler({
  onClose,
  setFormatFileLoaded,
}: {
  onClose: () => void;
  setFormatFileLoaded?: (
    nextState: boolean | ((prevState: boolean) => boolean)
  ) => void;
}): React.JSX.Element {
  const { dataCatalog, setReportFormatFile } = useBusinessReportEditor();
  const { restoreMappingProperties } = useMappingProperty();
  const { draftLoopRange, restoreLoops, setDraftLoopRange } = useLoop();
  const { spread, isSelectingLoopHeaderRow, setIsSelectingLoopHeaderRow } =
    useSpread();
  const { setError } = useError();

  const [selectedCategories, setSelectedCategories] = useState<Option[]>([]);
  const [selectedItem, setSelectedItem] = useState<Property | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const categories = useMemo(
    () => (!_.isNull(dataCatalog) ? dataCatalog.categories : []),
    [dataCatalog]
  );

  const activeCategories = useMemo(
    () =>
      _.filter(categories, (cat) =>
        _.some(
          selectedCategories,
          (sCat) => sCat.value === String(cat.categoryId)
        )
      ),
    [categories, selectedCategories]
  );

  const activeProperties: PropertyView[] = useMemo(
    () =>
      _.flatten(
        _.map(activeCategories, (cat) =>
          _.map(cat.properties, (prop) => ({
            ...prop,
            categoryKey: cat.categoryKey,
            categoryId: cat.categoryId,
            mappingRestriction:
              cat.mappingRestriction || MAPPING_RESTRICTION.AND_OPTION,
          }))
        )
      ),
    [activeCategories]
  );

  const isPropertySelected = useCallback(
    (item: PropertyView) => _.isEqual(item, selectedItem),
    [selectedItem]
  );

  const uploadUniqueFormat = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!_.isNull(e.target.files) && e.target.files!.length > 0) {
        if (setFormatFileLoaded) setFormatFileLoaded(false);

        const fileName = e.target.files[0].name;
        const lastDotIndex = fileName.lastIndexOf('.');

        if (
          !ALLOWED_FILE_TYPES.includes(
            fileName.slice(lastDotIndex + 1).toLowerCase()
          )
        ) {
          setError({
            title: translate('ERROR.FAILED_TO_UPLOAD_FILE_TITLE'),
            message: translate('ERROR.FAILED_TO_UPLOAD_FILE'),
          });
          return;
        }

        approvalFileType(e.target.files[0], [FILE_TYPE_EXCEL_XLSX])
          .then(() => {
            setReportFormatFile({ file: e.target.files![0], source: 'upload' });
            restoreMappingProperties([]);
            restoreLoops([]);
          })
          .catch(() => {
            setError({
              title: translate('ERROR.FAILED_TO_UPLOAD_FILE_TITLE'),
              message: translate('ERROR.FAILED_TO_INVALID_FILE_TYPE'),
            });
          });
      }
    },
    []
  );

  /**
   * 明細範囲の作成
   */
  const createLoopRange = () => {
    if (!spread) return;

    const sheet = spread.getActiveSheet();
    const selections = sheet.getSelections();

    if (_.isEmpty(selections)) return;

    const targetRange = selections[0];

    const ret = sheet?.getSpans(targetRange)[0];
    if (
      ret != undefined &&
      targetRange.contains(ret.row, ret.col, ret.rowCount, ret.colCount)
    ) {
      // 明細範囲は2行以上でないとループの設定ができない（セル結合されている場合）
      const rowCount = targetRange.rowCount - ret.rowCount + 1;
      if (rowCount <= 1) {
        setError({
          title: translate('ERROR.FAILED_TO_SET_LOOP_RANGE'),
          message: translate('ERROR.AT_LEAST_ONE_LINE_IS_REQUIRED'),
        });
        return;
      }
    }

    // 明細範囲は2行以上でないとループの設定ができない
    if (targetRange.rowCount <= 1) {
      setError({
        title: translate('ERROR.FAILED_TO_SET_LOOP_RANGE'),
        message: translate('ERROR.AT_LEAST_ONE_LINE_IS_REQUIRED'),
      });
      return;
    }

    const range = new GC.Spread.Sheets.Range(
      targetRange.row,
      targetRange.col,
      targetRange.rowCount,
      targetRange.colCount
    );

    // 明細範囲同士の交差がある場合は処理を中断する
    if (isRangeIntersectingAnyLoop(sheet, range)) {
      setError({
        title: translate('ERROR.FAILED_TO_SET_LOOP_RANGE'),
        message: translate('ERROR.DUPLICATE_LOOP_RANGE'),
      });
      return;
    }

    const commandManager = spread.commandManager();

    commandManager.execute({
      cmd: COMMAND_MANAGER_REGISTER_PROPERTY.CREATE_LOOP,
      sheetName: sheet.name(),
      applyFunc: () => {
        // 明細範囲のヘッダー行確定前なので一時的な保管にする
        setDraftLoopRange(targetRange);

        sheet.suspendPaint();

        const rowStart = targetRange.row;
        const rowEnd = targetRange.row + targetRange.rowCount - 1;
        const colStart = targetRange.col;
        const colEnd = targetRange.col + targetRange.colCount - 1;

        // 明細範囲内に設定済みのプロパティを削除する
        for (let row = rowStart; row < rowEnd; row++) {
          for (let col = colStart; col < colEnd; col++) {
            removePropertyByCell(sheet, row, col);
            // プロパティがない場合もセルコンテンツは削除
            sheet.getCell(row, col).clear(GC.Spread.Sheets.StorageType.data);
            // プロパティがない場合も背景色は削除
            clearCellBackground(sheet, row, col);
          }
        }

        // 背景色の変更
        applyLoopBackground(sheet, {
          startRow: rowStart,
          startCol: colStart,
          endRow: rowEnd,
          endCol: colEnd,
        });

        sheet.resumePaint();

        setIsSelectingLoopHeaderRow(true);
      },
      revertFunc: () => {
        // 明細範囲の一時的な保管を削除
        setDraftLoopRange(null);

        setIsSelectingLoopHeaderRow(false);
      },
    });
  };

  const confirmLoopHeaderRow = () => {
    if (!spread) return;

    const sheet = spread.getActiveSheet();
    const selections = sheet.getSelections();

    if (_.isEmpty(selections)) return;

    const targetRange = selections[0];

    if (!draftLoopRange) return;

    // 選択範囲が明細範囲内の左上スタートであることをチェック
    if (
      targetRange.row !== draftLoopRange.row ||
      targetRange.col !== draftLoopRange.col
    ) {
      setError({
        title: '',
        message: translate('ERROR.START_CELL_REQUIRED'),
      });
      return;
    }

    // 選択範囲が明細範囲内に収まっていることをチェック
    if (!isRangeContainedInRange(targetRange, draftLoopRange)) {
      setError({
        title: '',
        message: translate('ERROR.WITHIN_LOOP_RANGE'),
      });
      return;
    }

    const commandManager = spread.commandManager();

    commandManager.execute({
      cmd: COMMAND_MANAGER_REGISTER_PROPERTY.CREATE_LOOP_HEADER,
      sheetName: sheet.name(),
      applyFunc: () => {
        // 明細範囲の追加
        addLoop(sheet, {
          loopId: createLoopId(sheet),
          startRow: draftLoopRange.row,
          endRow: draftLoopRange.rowCount + draftLoopRange.row - 1,
          startCol: draftLoopRange.col,
          endCol: draftLoopRange.colCount + draftLoopRange.col - 1,
          headerEndRow: targetRange.row + targetRange.rowCount - 1,
          headerEndCol: targetRange.col + targetRange.colCount - 1,
        });

        setIsSelectingLoopHeaderRow(false);
      },
      revertFunc: () => {
        setIsSelectingLoopHeaderRow(true);
      },
    });
  };

  const cancelSelectingLoopRange = useCallback(() => {
    if (!spread) return;

    const undoManager = spread.undoManager();
    if (undoManager.canUndo && undoManager.canUndo()) {
      undoManager.undo();
    }

    setIsSelectingLoopHeaderRow(false);
  }, [spread]);

  return (
    <S.DataCatalogHandlerWrapper>
      <Grid container flexDirection="column" height="100%">
        <Grid container size="auto" alignItems="center" px={1.5} py={1}>
          <Grid size="grow">
            <Typography variant="h4">{translate('DATA_CATALOG')}</Typography>
          </Grid>
          <Grid size="auto">
            <IconButton
              onClick={() => onClose()}
              sx={(theme) => ({
                color: theme.palette.text.primary,
                padding: 0.5,
              })}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <S.Divider />
        <Grid container flexDirection="column" size="grow">
          <Grid
            size="grow"
            pl={1.5}
            pt={2}
            sx={{ overflowY: 'scroll', height: '100%' }}
          >
            <Box mb={2}>
              <S.SectionTitle>1. {translate('CATEGORIES')}</S.SectionTitle>
              <MultiSelect
                options={_.map(categories, (cat) => ({
                  value: String(cat.categoryId),
                  label: cat.categoryName,
                }))}
                value={_.map(selectedCategories, (cat) => cat.value)}
                onChange={(values) => setSelectedCategories(values)}
                disabled={isSelectingLoopHeaderRow}
              />
            </Box>
            <Box mb={2}>
              <S.SectionTitle>2. {translate('OUTPUT_ITEMS')}</S.SectionTitle>
              {!isSelectingLoopHeaderRow && (
                <List sx={{ padding: 0 }}>
                  {_.map(activeProperties, (item) => (
                    <S.ListItemButton
                      key={item.propertyId}
                      selected={isPropertySelected(item)}
                      onClick={() => setSelectedItem(item)}
                    >
                      <Grid container alignItems="center" spacing={1}>
                        <Grid size="grow">
                          <DataCatalogDragBox item={item}>
                            <Typography
                              sx={{
                                wordBreak: 'break-all',
                                fontWeight: isPropertySelected(item)
                                  ? 700
                                  : 400,
                              }}
                            >
                              {item.propertyName}
                            </Typography>
                          </DataCatalogDragBox>
                        </Grid>
                        <Grid size="auto">
                          <DraggableIcon
                            fontSize="small"
                            sx={(theme) => ({
                              display: 'block',
                              color: isPropertySelected(item)
                                ? theme.palette.text.primary
                                : theme.palette.text.disabled,
                            })}
                          />
                        </Grid>
                      </Grid>
                    </S.ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Grid>
          {isSelectingLoopHeaderRow && (
            <Grid
              container
              flexDirection="column"
              px={1.5}
              py={2}
              height={120}
              justifyContent="space-between"
            >
              <Grid size="auto">
                <Typography
                  sx={{
                    wordBreak: 'break-all',
                  }}
                >
                  {translate('SET_MAPPING_ENABLE_CELL_RANGE')}
                </Typography>
              </Grid>
              <Grid container justifyContent="space-between" size="auto">
                <Grid>
                  <Button
                    color="warning"
                    variant="contained"
                    onClick={cancelSelectingLoopRange}
                    sx={{ width: 160, fontSize: 12 }}
                    disabled={!isSelectingLoopHeaderRow}
                  >
                    {translate('COMMON.CANCEL')}
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={confirmLoopHeaderRow}
                    sx={{ width: 160, fontSize: 12 }}
                    disabled={!isSelectingLoopHeaderRow}
                  >
                    {translate('COMMON.CONFIRM')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
          <S.Divider />
          <Grid
            container
            flexDirection="column"
            px={1.5}
            py={2}
            width="100%"
            height={280}
            justifyContent="space-between"
          >
            <Grid size="auto" width="100%">
              <S.SectionTitle>
                {`3. ${translate('SELECTED_ITEM')}：${!isSelectingLoopHeaderRow && selectedItem ? selectedItem.propertyName : ''}`}
              </S.SectionTitle>
            </Grid>
            {!isSelectingLoopHeaderRow && (
              <Grid
                container
                flexDirection="column"
                size="grow"
                height={165}
                sx={(theme) => ({
                  marginBottom: 2,
                  border: `2px solid ${theme.palette.primary.main}`,
                })}
              >
                <Grid
                  p={1}
                  height="65%"
                  sx={(theme) => ({
                    borderBottom: `1px solid ${theme.palette.border.main}`,
                    overflowY: 'scroll',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  })}
                >
                  <Typography>
                    {selectedItem ? selectedItem.propertyDescription : ''}
                  </Typography>
                </Grid>
                <Grid
                  p={1}
                  height="35%"
                  sx={{
                    overflowY: 'scroll',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography>
                    {selectedItem ? selectedItem.propertyExample : ''}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid container justifyContent="space-between" size="auto">
              <Grid>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={createLoopRange}
                  sx={{ width: 160, fontSize: 12 }}
                  disabled={isSelectingLoopHeaderRow}
                >
                  {translate('SET_LOOP_RANGE')}
                </Button>
              </Grid>
              <Grid>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ width: 160, fontSize: 12 }}
                  disabled={isSelectingLoopHeaderRow}
                >
                  {translate('UPLOAD_CUSTOM_TEMPLATE')}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={uploadUniqueFormat}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </S.DataCatalogHandlerWrapper>
  );
}
